import * as React from 'react';
import { TrendingUp, TrendingDown, Minus, type LucideIcon } from 'lucide-react';

import { Card } from '../primitives/card';
import { Skeleton } from '../primitives/skeleton';
import { Sparkline } from './charts/sparkline';
import { cn } from '../lib/utils';

/** Semantic tone names a dashboard may pass; mapped onto the internal ramp. */
export type KpiTone =
  | 'default' | 'neutral'
  | 'good' | 'success'
  | 'warn' | 'warning'
  | 'bad' | 'danger' | 'critical'
  | 'info';

export interface KpiCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** KPI title */
  title?: string;
  /** Alias for `title` — accepted so stat-strip call sites can pass `label`. */
  label?: string;
  /**
   * Explicit tone override. When omitted, tone derives from `threshold`
   * (or stays neutral). Lets dashboard tiles color a card by status directly.
   */
  tone?: KpiTone;
  /** Primary metric value */
  value: number | string;
  /** Unit suffix (e.g., %, bph) */
  unit?: string;
  /** Optional subtitle / context line */
  subtitle?: string;
  /** Optional icon shown on the left of the card */
  icon?: LucideIcon;
  /** Trend indicator (rendered as a pill in the top-right) */
  trend?: {
    direction: 'up' | 'down' | 'flat';
    percentage: number;
  };
  /** Threshold-based color coding (0-100) */
  threshold?: {
    good: number;
    warn: number;
  };
  /** Numeric value used for threshold comparison (defaults to `value` parsed) */
  compareValue?: number;
  /** Optional sparkline data points (rendered full-width below the value) */
  sparkline?: number[];
  /** Loading state — shows a skeleton */
  loading?: boolean;
  /**
   * Drill affordance. When provided, the card becomes an interactive button:
   * it gains `role="button"`, keyboard focus + Enter/Space activation, and a
   * pointer/hover treatment — so a KPI can navigate to its filtered detail
   * list/route. Omit on cards with no sensible drill target so they stay inert.
   */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

type ToneKey = 'good' | 'warn' | 'bad' | 'neutral';

const toneClasses: Record<ToneKey, { value: string; iconBg: string; iconFg: string; trend: string }> = {
  good: {
    value: 'text-[hsl(var(--state-running))]',
    iconBg: 'bg-[hsl(var(--state-running))]/10',
    iconFg: 'text-[hsl(var(--state-running))]',
    trend: 'bg-[hsl(var(--state-running))]/10 text-[hsl(var(--state-running))]',
  },
  warn: {
    value: 'text-[hsl(var(--alarm-minor))]',
    iconBg: 'bg-[hsl(var(--alarm-minor))]/10',
    iconFg: 'text-[hsl(var(--alarm-minor))]',
    trend: 'bg-[hsl(var(--alarm-minor))]/10 text-[hsl(var(--alarm-minor))]',
  },
  bad: {
    value: 'text-[hsl(var(--alarm-critical))]',
    iconBg: 'bg-[hsl(var(--alarm-critical))]/10',
    iconFg: 'text-[hsl(var(--alarm-critical))]',
    trend: 'bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))]',
  },
  neutral: {
    value: 'text-foreground',
    iconBg: 'bg-muted',
    iconFg: 'text-muted-foreground',
    trend: 'bg-muted text-muted-foreground',
  },
};

/** Map the card tone onto the canonical Sparkline color token. */
const toneSparklineColor: Record<
  ToneKey,
  'phosphor' | 'state-running' | 'alarm-major' | 'foreground'
> = {
  good: 'state-running',
  warn: 'alarm-major',
  bad: 'alarm-major',
  neutral: 'foreground',
};

/** Collapse the public semantic tone names onto the internal four-key ramp. */
function normalizeTone(tone: KpiTone): ToneKey {
  switch (tone) {
    case 'good':
    case 'success':
      return 'good';
    case 'warn':
    case 'warning':
    case 'info':
      return 'warn';
    case 'bad':
    case 'danger':
    case 'critical':
      return 'bad';
    default:
      return 'neutral';
  }
}

function resolveTone(
  value: number | string,
  threshold?: KpiCardProps['threshold'],
  compareValue?: number
): ToneKey {
  if (!threshold) return 'neutral';
  const numeric =
    compareValue ??
    (typeof value === 'string' ? parseFloat(value) : value);
  if (Number.isNaN(numeric)) return 'neutral';
  if (numeric >= threshold.good) return 'good';
  if (numeric >= threshold.warn) return 'warn';
  return 'bad';
}

/**
 * Manufacturing KPI card for dashboard tiles (OEE, throughput, FPY, etc.).
 *
 * Layout (info-dense, icon-left):
 *   ┌─────────────────────────────────┐
 *   │ [ICON]  TITLE          [trend]  │
 *   │ [ICON]  VALUE unit              │
 *   │ ─────────────────────────────── │
 *   │ subtitle (optional)             │
 *   │ sparkline (optional)            │
 *   └─────────────────────────────────┘
 */
export const KpiCard = React.forwardRef<HTMLDivElement, KpiCardProps>(
  (
    {
      title,
      label,
      tone: toneProp,
      value,
      unit,
      subtitle,
      icon: Icon,
      trend,
      threshold,
      compareValue,
      sparkline,
      loading = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const heading = title ?? label ?? '';
    const tone = toneProp ? normalizeTone(toneProp) : resolveTone(value, threshold, compareValue);
    const toneStyle = toneClasses[tone];

    const TrendIcon =
      trend?.direction === 'up'
        ? TrendingUp
        : trend?.direction === 'down'
        ? TrendingDown
        : Minus;

    // Interactive drill affordance: mirror the dashboard-tile pattern —
    // role="button" + keyboard focus + Enter/Space activation + a pointer /
    // hover treatment. Inert (no semantics) when `onClick` is omitted.
    const interactive = Boolean(onClick);
    const interactiveProps: React.HTMLAttributes<HTMLDivElement> = interactive
      ? {
          onClick,
          role: 'button',
          tabIndex: 0,
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.(e as unknown as React.MouseEvent<HTMLDivElement>);
            }
          },
        }
      : {};

    return (
      <Card
        ref={ref}
        className={cn(
          'overflow-hidden p-4',
          interactive &&
            'group cursor-pointer transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className,
        )}
        {...interactiveProps}
        {...props}
      >
        <div className="flex items-start gap-3">
          {Icon && (
            <div
              className={cn(
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
                toneStyle.iconBg
              )}
            >
              <Icon className={cn('h-5 w-5', toneStyle.iconFg)} />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground truncate">
                {heading}
              </p>
              {trend && !loading && (
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium',
                    toneStyle.trend
                  )}
                >
                  <TrendIcon className="h-3 w-3" />
                  {Math.abs(trend.percentage).toFixed(1)}%
                </span>
              )}
            </div>

            {loading ? (
              <Skeleton className="mt-1 h-7 w-20" />
            ) : (
              <div className="mt-0.5 flex items-baseline gap-1">
                <span className={cn('font-heading text-2xl font-bold leading-tight tabular-nums', toneStyle.value)}>
                  {value}
                </span>
                {unit && (
                  <span className="text-sm font-medium text-muted-foreground">
                    {unit}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {(subtitle || (sparkline && sparkline.length > 1)) && !loading && (
          <div className="mt-2 space-y-1.5">
            {subtitle && (
              <p className="text-xs text-muted-foreground line-clamp-1">{subtitle}</p>
            )}
            {sparkline && sparkline.length > 1 && (
              <Sparkline
                data={sparkline}
                color={toneSparklineColor[tone]}
                width={120}
                height={24}
                showLatest={false}
                className="w-full"
                ariaLabel={`${heading} trend`}
              />
            )}
          </div>
        )}
      </Card>
    );
  }
);

KpiCard.displayName = 'KpiCard';
